import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_intelligence-rebuilt/artifacts/jhoeqook_ProcureAide%20Logo%20with%20Ai.png";

const navLinks = [
  { name: "Platform", path: "/platform" },
  { 
    name: "Products", 
    path: "/products",
    submenu: [
      { name: "DataRefinery", path: "/products/data-refinery" },
      { name: "SpendLens", path: "/products/spendlens" },
      { name: "ConTrack", path: "/products/contrack" },
    ]
  },
  { name: "How It Works", path: "/how-it-works" },
  { name: "Industries", path: "/industries" },
  { name: "Insights", path: "/insights" },
  { name: "About", path: "/about" },
  { name: "Community", path: "/community" },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#020617]/80 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <div className="bg-white rounded-lg px-3 py-1.5">
              <img 
                src={LOGO_URL} 
                alt="Procureaide" 
                className="h-8 w-auto"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => link.submenu && setActiveDropdown(link.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={link.path}
                  className={`nav-link flex items-center gap-1 text-sm font-medium ${
                    location.pathname.startsWith(link.path) ? "active" : ""
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                  {link.submenu && <ChevronDown className="w-4 h-4" />}
                </Link>

                {/* Dropdown */}
                {link.submenu && (
                  <AnimatePresence>
                    {activeDropdown === link.name && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-56 rounded-xl bg-[#0B1426] border border-[#00B4D8]/20 shadow-xl overflow-hidden"
                      >
                        {link.submenu.map((sublink) => (
                          <Link
                            key={sublink.name}
                            to={sublink.path}
                            className="block px-4 py-3 text-sm text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#111F36] transition-colors"
                            data-testid={`nav-submenu-${sublink.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {sublink.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="mailto:info@procureaide.com">
              <Button 
                className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] hover:scale-105 transition-all shadow-[0_0_20px_rgba(0,180,216,0.3)]"
                data-testid="nav-book-demo-btn"
              >
                Book Demo
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2 text-[#F8FAFC]"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden mobile-menu border-t border-white/10"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <div key={link.name}>
                  <Link
                    to={link.path}
                    className={`block py-2 text-base font-medium ${
                      location.pathname.startsWith(link.path)
                        ? "text-[#00B4D8]"
                        : "text-[#94A3B8]"
                    }`}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                  {link.submenu && (
                    <div className="pl-4 mt-2 space-y-2">
                      {link.submenu.map((sublink) => (
                        <Link
                          key={sublink.name}
                          to={sublink.path}
                          className="block py-1 text-sm text-[#64748B] hover:text-[#F8FAFC]"
                          data-testid={`mobile-nav-submenu-${sublink.name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {sublink.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4">
                <a href="mailto:info@procureaide.com">
                  <Button 
                    className="w-full bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4]"
                    data-testid="mobile-nav-book-demo-btn"
                  >
                    Book Demo
                  </Button>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
